import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { styled } from '@core-ui/react-stitch';
import { BreakpointContext } from '@core-ui/breakpoint-provider';
import { GridContextProvider } from '../GridContext';
import { GridContainerProps } from './types';
import { getColumnsForScreensize, getCustomWidths } from './custom-width-helpers';

const GridContainerSection = styled.div(() => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap'
}));

const GridContainer = (props: GridContainerProps): ReactElement => {
  const {
    children,
    columns,
    isBRRedesignEnabled,
  } = props;
  const { size } = useContext(BreakpointContext);
  const [innerWidth, setInnerWidth] = useState(0);
  const [numberOfColumns, setNumberOfColumns] = useState<number>(2);
  const customWidths = getCustomWidths(columns);

  const onWindowResize = useCallback((): void => {
    const newWidth = getColumnsForScreensize(innerWidth, customWidths);
    if (newWidth === innerWidth) return;
    setInnerWidth(newWidth);
  }, [customWidths, innerWidth]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (isBRRedesignEnabled) {
      setNumberOfColumns(columns[size] || columns[innerWidth]); 
      return; 
    }

    setNumberOfColumns(columns[innerWidth] || columns[size]); 
  }, [columns, innerWidth, size, isBRRedesignEnabled]);

  useEffect(() => {
    onWindowResize();

    window.removeEventListener('resize', onWindowResize);
    window.addEventListener('resize', onWindowResize);

    return (): void => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [innerWidth, onWindowResize]);

  return (
    <GridContextProvider
      value={{ size, numberOfColumns }}
    >
      <GridContainerSection>
        {children}
      </GridContainerSection>
    </GridContextProvider>
  );
};

export default GridContainer;
